import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaExclamationTriangle, FaInfoCircle, FaPlay } from 'react-icons/fa';
import './DebugConsoleTab.css';

interface DebugConsoleTabProps {
  logs: DebugLog[];
  input: string;
  onInputChange: (value: string) => void;
  onInputSubmit: (command: string) => void;
  onClear?: () => void;
}

export interface DebugLog {
  id: string;
  type: 'log' | 'error' | 'warn' | 'info' | 'debug';
  message: string;
  timestamp: Date;
}

const DebugConsoleTab: React.FC<DebugConsoleTabProps> = ({
  logs,
  input,
  onInputChange,
  onInputSubmit,
  onClear
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [logs]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      onInputSubmit(input);
      onInputChange('');
    }
  };

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'error': return <FaTimes size={12} />;
      case 'warn': return <FaExclamationTriangle size={12} />;
      case 'info': return <FaInfoCircle size={12} />;
      case 'debug': return <FaPlay size={10} />;
      default: return <FaPlay size={10} />;
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  return (
    <div className="debug-console-tab-container">
      <div className="debug-console-toolbar">
        <div className="debug-console-filters">
          <button className="filter-btn active" title={t('debug.showAll', 'Show All')}>
            All
          </button>
          <button className="filter-btn" title={t('debug.showErrors', 'Show Errors')}>
            <FaTimes size={11} />
          </button>
          <button className="filter-btn" title={t('debug.showWarnings', 'Show Warnings')}>
            <FaExclamationTriangle size={11} />
          </button>
          <button className="filter-btn" title={t('debug.showInfo', 'Show Info')}>
            <FaInfoCircle size={11} />
          </button>
        </div>
        {onClear && (
          <button className="debug-clear-btn" onClick={onClear} title={t('debug.clear', 'Clear Console')}>
            <FaTimes size={12} />
          </button>
        )}
      </div>
      <div className="debug-console-content" ref={contentRef}>
        {logs.length === 0 ? (
          <div className="debug-console-empty">
            {t('debug.noLogs', 'No debug logs yet...')}
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className={`debug-log-entry ${log.type}`}>
              <span className="log-icon">{getLogIcon(log.type)}</span>
              <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
              <span className="log-message">{log.message}</span>
            </div>
          ))
        )}
      </div>
      <div className="debug-console-input-area">
        <span className="input-prompt">&gt;</span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="debug-console-input"
          placeholder={t('debug.inputPlaceholder', 'Evaluate expression...')}
        />
      </div>
    </div>
  );
};

export default DebugConsoleTab;
